import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GoogleAdsPreviewModal from './GoogleAdsPreviewModal';
import ErrorBoundary from './ErrorBoundary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoogleAdsGeneratorPage = ({ locationId }) => {
    const [locationData, setLocationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('create'); // 'create' or 'previous'
    const [previousCampaigns, setPreviousCampaigns] = useState([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    
    // Form data
    const [formData, setFormData] = useState({
        targeting_radius: 5,
        budget: 30.00,
        final_url: '',  // Changed from auto-populated to empty
        campaign_target: '',  // Required: Kids, Adults, Both
        business_type: '',    // Required: Martial Arts subcategory, Gym subcategory, etc.
        other_business_type: '',  // Only used when business_type is "Other"
        address_override: '',  // Editable address used for geocoding; does NOT change GHL/TFUN data
        style_override: '',    // Editable style used for ad copy; does NOT change GHL/TFUN data
        studio_name_override: '' // Editable studio name used for ad copy; does NOT change GHL/TFUN data
    });
    
    const [urlValidation, setUrlValidation] = useState({ valid: null, checking: false });
    
    // Colors for consistency with dashboard
    const colors = {
        primary: '#1e40af',
        secondary: '#f3f4f6',
        card: '#ffffff',
        border: '#e5e7eb',
        text: '#1f2937',
        textLight: '#6b7280',
        success: '#059669',
        warning: '#d97706',
        error: '#dc2626'
    };
    
    useEffect(() => {
        loadLocationData();
        if (activeTab === 'previous') {
            loadPreviousCampaigns();
        }
    }, [locationId, activeTab]);
    
    const validateUrl = async (url) => {
        if (!url || url.trim() === '') {
            setUrlValidation({ valid: null, checking: false });
            return;
        }
        
        // Basic URL format validation
        const urlPattern = /^https?:\/\/.+\..+/i;
        if (!urlPattern.test(url)) {
            setUrlValidation({ valid: false, checking: false, message: 'Please enter a valid URL (e.g., https://www.example.com)' });
            return;
        }
        
        setUrlValidation({ valid: null, checking: true });
        
        try {
            // Simple check - try to fetch headers to see if URL is reachable
            const response = await fetch(url, { method: 'HEAD', mode: 'no-cors' });
            setUrlValidation({ valid: true, checking: false, message: 'URL looks good!' });
        } catch (err) {
            // no-cors mode doesn't give us actual error info, so just mark as valid if format is correct
            setUrlValidation({ valid: true, checking: false, message: 'URL format is valid' });
        }
    };

    const handleDeleteCampaign = async (campaignId, campaignName) => {
        if (window.confirm(`Are you sure you want to delete "${campaignName}"? This action cannot be undone.`)) {
            try {
                await axios.delete(`${API}/google-ads/campaigns/${locationId}/${campaignId}`);
                // Reload campaigns list to reflect deletion
                loadPreviousCampaigns();
                alert('Campaign deleted successfully!');
            } catch (err) {
                console.error('Error deleting campaign:', err);
                alert('Failed to delete campaign. Please try again.');
            }
        }
    };

    const loadPreviousCampaigns = async () => {
        try {
            setCampaignsLoading(true);
            const response = await axios.get(`${API}/google-ads/campaigns/${locationId}/list`);
            setPreviousCampaigns(response.data);
        } catch (err) {
            console.error('Error loading previous campaigns:', err);
            // Don't show error for campaigns - just show empty list
            setPreviousCampaigns([]);
        } finally {
            setCampaignsLoading(false);
        }
    };

    const handleSelectCampaign = async (campaignId) => {
        try {
            const response = await axios.get(`${API}/google-ads/campaigns/${locationId}/${campaignId}`);
            setSelectedCampaign(response.data);
            setModalOpen(true);
        } catch (err) {
            console.error('Error loading campaign:', err);
            alert('Failed to load campaign. Please try again.');
        }
    };

    const handleSaveAsNew = async (campaignId) => {
        try {
            const response = await axios.post(`${API}/google-ads/campaigns/${locationId}/${campaignId}/save-as-new`);
            // Reload campaigns list to show new campaign
            loadPreviousCampaigns();
            // Open the new campaign in modal
            setSelectedCampaign(response.data);
            setModalOpen(true);
            alert('Campaign copied successfully with today\'s date!');
        } catch (err) {
            console.error('Error saving as new campaign:', err);
            alert('Failed to copy campaign. Please try again.');
        }
    };
    
    const handleUrlChange = (e) => {
        const url = e.target.value;
        setFormData(prev => ({ ...prev, final_url: url }));
        
        // Debounce validation
        clearTimeout(window.urlValidationTimeout);
        window.urlValidationTimeout = setTimeout(() => {
            validateUrl(url);
        }, 1000);
    };

    const loadLocationData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await axios.get(`${API}/google-ads/location-data/${locationId}`);
            
            if (response.data.success) {
                setLocationData(response.data.data);
                // Hydrate the editable overrides:
                //   1) keep anything the user already typed this session
                //   2) otherwise use the persisted override returned by the API
                //   3) finally fall back to the raw GHL value
                const ghl = response.data.data || {};
                setFormData(prev => ({
                    ...prev,
                    address_override: prev.address_override
                        || ghl.address_override
                        || ghl.full_address
                        || '',
                    style_override: prev.style_override
                        || ghl.style_override
                        || ghl.style
                        || '',
                    studio_name_override: prev.studio_name_override
                        || ghl.studio_name_override
                        || ghl.studio_name
                        || ''
                }));
                // Removed auto-population of final_url - user must enter manually
            } else {
                setError('Failed to load studio data');
            }
        } catch (err) {
            console.error('Error loading location data:', err);
            
            // Check if this is a company authentication issue
            const errorDetail = err.response?.data?.detail || '';
            if (errorDetail.includes('No Company token available') || errorDetail.includes('company')) {
                setError({
                    type: 'company_auth_required',
                    message: 'Company-level authentication is required for the Google Ads Campaign Generator',
                    details: 'The Google Ads feature requires additional permissions to access your location data. Please re-authenticate with company-level access.',
                    authUrl: `${window.location.origin}/api/oauth/authorize-agency`
                });
            } else if (errorDetail.includes('LOCATION_NOT_ACTIVE') || errorDetail.includes('Location is not active')) {
                setError({
                    type: 'location_not_active',
                    rawDetail: errorDetail
                });
            } else if (errorDetail.includes('LOCATION_NOT_AUTHORIZED') || errorDetail.includes('Failed to generate location token')) {
                setError({
                    type: 'location_not_authorized',
                    message: 'This sub-account has not authorized The Follow Up Ninja for Google Ads.',
                    details: (
                        'Other sub-accounts work because they were connected when you installed the app. '
                        + 'To fix this one, either install the app on this specific sub-account from inside GHL '
                        + '(Settings → Integrations → Marketplace), or reconnect the agency-level OAuth so the '
                        + 'newly added sub-account is included in the agency token.'
                    ),
                    authUrl: `${window.location.origin}/api/oauth/authorize-agency`,
                    rawDetail: errorDetail
                });
            } else {
                setError({
                    type: 'general',
                    message: err.response?.data?.detail || 'Failed to load studio data',
                    details: null,
                    authUrl: null
                });
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Check if all required fields are filled
    const isFormValid = () => {
        return formData.final_url && 
               formData.campaign_target && 
               formData.business_type &&
               (formData.business_type !== 'Other' || formData.other_business_type.trim() !== '');
    };
    
    
    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: colors.secondary,
                fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎯</div>
                    <h2 style={{ color: colors.text, marginBottom: '8px' }}>Loading Studio Data...</h2>
                </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: colors.secondary,
                fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    textAlign: 'center',
                    backgroundColor: colors.card,
                    padding: '40px',
                    borderRadius: '16px',
                    border: `1px solid ${colors.border}`,
                    maxWidth: '600px'
                }}>
                    {error.type === 'company_auth_required' ? (
                        <>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
                            <h2 style={{ color: colors.warning, marginBottom: '8px' }}>
                                Authentication Required
                            </h2>
                            <p style={{ color: colors.textLight, marginBottom: '16px' }}>
                                {error.message}
                            </p>
                            <p style={{ 
                                color: colors.textLight, 
                                marginBottom: '24px', 
                                fontSize: '14px',
                                backgroundColor: '#fff3cd',
                                padding: '12px',
                                borderRadius: '8px',
                                border: '1px solid #ffeaa7'
                            }}>
                                {error.details}
                            </p>
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                                <a
                                    href={error.authUrl}
                                    style={{
                                        backgroundColor: colors.primary,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 24px',
                                        textDecoration: 'none',
                                        fontFamily: 'Poppins',
                                        fontWeight: '500',
                                        display: 'inline-block'
                                    }}
                                >
                                    🔐 Authenticate with Company Access
                                </a>
                                <button
                                    onClick={loadLocationData}
                                    style={{
                                        backgroundColor: colors.border,
                                        color: colors.text,
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 24px',
                                        cursor: 'pointer',
                                        fontFamily: 'Poppins',
                                        fontWeight: '500'
                                    }}
                                >
                                    Try Again
                                </button>
                            </div>
                        </>
                    ) : error.type === 'location_not_active' ? (
                        <>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏸️</div>
                            <h2 style={{ color: colors.warning, marginBottom: '16px' }}>
                                Account Not Active
                            </h2>
                            <p style={{
                                color: colors.text,
                                marginBottom: '20px',
                                fontSize: '15px',
                                lineHeight: '1.5'
                            }}>
                                This account is not active. Please submit a support ticket to re-activate your account at{' '}
                                <a
                                    href="https://support.thefollowupninja.com"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="google-ads-support-link"
                                    style={{
                                        color: colors.primary,
                                        textDecoration: 'underline',
                                        fontWeight: '500'
                                    }}
                                >
                                    https://support.thefollowupninja.com
                                </a>
                            </p>
                            {error.rawDetail && (
                                <details style={{
                                    marginBottom: '20px',
                                    fontSize: '12px',
                                    color: colors.textLight,
                                    textAlign: 'left'
                                }}>
                                    <summary style={{ cursor: 'pointer' }}>Technical details</summary>
                                    <pre style={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        backgroundColor: colors.secondary,
                                        padding: '8px',
                                        borderRadius: '4px',
                                        marginTop: '8px'
                                    }}>{error.rawDetail}</pre>
                                </details>
                            )}
                            <button
                                onClick={loadLocationData}
                                style={{
                                    backgroundColor: colors.border,
                                    color: colors.text,
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 24px',
                                    cursor: 'pointer',
                                    fontFamily: 'Poppins',
                                    fontWeight: '500'
                                }}
                            >
                                Try Again
                            </button>
                        </>
                    ) : error.type === 'location_not_authorized' ? (
                        <>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔌</div>
                            <h2 style={{ color: colors.warning, marginBottom: '8px' }}>
                                Sub-account Not Connected
                            </h2>
                            <p style={{ color: colors.textLight, marginBottom: '16px' }}>
                                {error.message}
                            </p>
                            <p style={{
                                color: colors.textLight,
                                marginBottom: '16px',
                                fontSize: '14px',
                                backgroundColor: '#fff3cd',
                                padding: '12px',
                                borderRadius: '8px',
                                border: '1px solid #ffeaa7',
                                textAlign: 'left'
                            }}>
                                {error.details}
                            </p>
                            {error.rawDetail && (
                                <details style={{
                                    marginBottom: '20px',
                                    fontSize: '12px',
                                    color: colors.textLight,
                                    textAlign: 'left'
                                }}>
                                    <summary style={{ cursor: 'pointer' }}>Technical details</summary>
                                    <pre style={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        backgroundColor: colors.secondary,
                                        padding: '8px',
                                        borderRadius: '4px',
                                        marginTop: '8px'
                                    }}>{error.rawDetail}</pre>
                                </details>
                            )}
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                <a
                                    href={error.authUrl}
                                    data-testid="google-ads-reconnect-agency-btn"
                                    style={{
                                        backgroundColor: colors.primary,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 24px',
                                        textDecoration: 'none',
                                        fontFamily: 'Poppins',
                                        fontWeight: '500',
                                        display: 'inline-block'
                                    }}
                                >
                                    🔄 Reconnect Agency OAuth
                                </a>
                                <button
                                    onClick={loadLocationData}
                                    style={{
                                        backgroundColor: colors.border,
                                        color: colors.text,
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 24px',
                                        cursor: 'pointer',
                                        fontFamily: 'Poppins',
                                        fontWeight: '500'
                                    }}
                                >
                                    Try Again
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                            <h2 style={{ color: colors.error, marginBottom: '8px' }}>Error</h2>
                            <p style={{ color: colors.textLight, marginBottom: '20px' }}>
                                {error.message || error}
                            </p>
                            <button
                                onClick={loadLocationData}
                                style={{
                                    backgroundColor: colors.primary,
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 24px',
                                    cursor: 'pointer',
                                    fontFamily: 'Poppins',
                                    fontWeight: '500'
                                }}
                            >
                                Try Again
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }
    
    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: colors.secondary,
            fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: colors.card,
                borderBottom: `1px solid ${colors.border}`,
                padding: '20px 32px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h1 style={{
                    color: colors.primary,
                    margin: 0,
                    fontSize: '28px',
                    fontWeight: '700'
                }}>
                    🎯 Google Ads Campaign Generator
                </h1>
            </div>

            {/* Tabs */}
            <div style={{
                backgroundColor: colors.card,
                borderBottom: `1px solid ${colors.border}`,
                padding: '0 32px'
            }}>
                <div style={{
                    display: 'flex',
                    gap: '0'
                }}>
                    <button
                        onClick={() => setActiveTab('create')}
                        style={{
                            padding: '16px 24px',
                            border: 'none',
                            backgroundColor: activeTab === 'create' ? colors.primary : 'transparent',
                            color: activeTab === 'create' ? 'white' : colors.textLight,
                            fontFamily: 'Poppins',
                            fontWeight: '600',
                            borderRadius: '8px 8px 0 0',
                            cursor: 'pointer',
                            borderBottom: activeTab === 'create' ? 'none' : `2px solid ${colors.border}`,
                            marginBottom: activeTab === 'create' ? '0' : '-1px'
                        }}
                    >
                        📝 Create New Campaign
                    </button>
                    <button
                        onClick={() => setActiveTab('previous')}
                        style={{
                            padding: '16px 24px',
                            border: 'none',
                            backgroundColor: activeTab === 'previous' ? colors.primary : 'transparent',
                            color: activeTab === 'previous' ? 'white' : colors.textLight,
                            fontFamily: 'Poppins',
                            fontWeight: '600',
                            borderRadius: '8px 8px 0 0',
                            cursor: 'pointer',
                            borderBottom: activeTab === 'previous' ? 'none' : `2px solid ${colors.border}`,
                            marginBottom: activeTab === 'previous' ? '0' : '-1px'
                        }}
                    >
                        📋 Previous Campaigns
                    </button>
                </div>
            </div>
            
            <div style={{ padding: '32px' }}>
                {activeTab === 'create' && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '32px',
                        maxWidth: '1200px',
                        margin: '0 auto'
                    }}>
                        {/* Form Section */}
                        <div>
                            {/* Studio Information */}
                            <div style={{
                                backgroundColor: colors.card,
                                borderRadius: '12px',
                                border: `1px solid ${colors.border}`,
                                padding: '24px',
                                marginBottom: '24px'
                            }}>
                                <h3 style={{
                                    color: colors.text,
                                    marginBottom: '16px',
                                    fontSize: '18px',
                                    fontWeight: '600'
                                }}>
                                    📍 Studio Information (Auto-Populated)
                                </h3>
                                <div style={{ display: 'grid', gap: '12px' }}>
                                    <div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'baseline',
                                            marginBottom: '4px'
                                        }}>
                                            <label style={{
                                                fontWeight: '500',
                                                color: colors.text
                                            }}>
                                                Studio Name:
                                            </label>
                                            {locationData?.studio_name && formData.studio_name_override !== locationData.studio_name && (
                                                <button
                                                    type="button"
                                                    data-testid="google-ads-reset-studio-name-btn"
                                                    onClick={() => handleInputChange('studio_name_override', locationData.studio_name)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: colors.primary,
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        fontFamily: 'Poppins',
                                                        padding: 0,
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    📋 Use detected TFUN name
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            type="text"
                                            data-testid="google-ads-studio-name-override-input"
                                            value={formData.studio_name_override}
                                            onChange={(e) => handleInputChange('studio_name_override', e.target.value)}
                                            placeholder={locationData?.studio_name || 'Loading...'}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontFamily: 'Poppins',
                                                backgroundColor: colors.card,
                                                color: colors.text,
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <small style={{ color: colors.textLight, display: 'block', marginTop: '4px' }}>
                                            Used in ad copy generation. Editing here won&apos;t change the studio name in TFUN.
                                        </small>
                                    </div>
                                    <div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'baseline',
                                            marginBottom: '4px'
                                        }}>
                                            <label style={{
                                                fontWeight: '500',
                                                color: colors.text
                                            }}>
                                                Address:
                                            </label>
                                            {locationData?.full_address && formData.address_override !== locationData.full_address && (
                                                <button
                                                    type="button"
                                                    data-testid="google-ads-reset-address-btn"
                                                    onClick={() => handleInputChange('address_override', locationData.full_address)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: colors.primary,
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        fontFamily: 'Poppins',
                                                        padding: 0,
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    📋 Use detected TFUN address
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            type="text"
                                            data-testid="google-ads-address-override-input"
                                            value={formData.address_override}
                                            onChange={(e) => handleInputChange('address_override', e.target.value)}
                                            placeholder={locationData?.full_address || 'Loading...'}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontFamily: 'Poppins',
                                                backgroundColor: colors.card,
                                                color: colors.text,
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <small style={{ color: colors.textLight, display: 'block', marginTop: '4px' }}>
                                            Used only for ad location targeting. Editing here won&apos;t change the address in TFUN. If you hit a &quot;Failed to geocode&quot; error, try removing the suite number.
                                        </small>
                                    </div>
                                    <div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'baseline',
                                            marginBottom: '4px'
                                        }}>
                                            <label style={{
                                                fontWeight: '500',
                                                color: colors.text
                                            }}>
                                                Style:
                                            </label>
                                            {locationData?.style && formData.style_override !== locationData.style && (
                                                <button
                                                    type="button"
                                                    data-testid="google-ads-reset-style-btn"
                                                    onClick={() => handleInputChange('style_override', locationData.style)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: colors.primary,
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        fontFamily: 'Poppins',
                                                        padding: 0,
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    📋 Use detected TFUN style
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            type="text"
                                            data-testid="google-ads-style-override-input"
                                            value={formData.style_override}
                                            onChange={(e) => handleInputChange('style_override', e.target.value)}
                                            placeholder={locationData?.style || 'Martial Arts'}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontFamily: 'Poppins',
                                                backgroundColor: colors.card,
                                                color: colors.text,
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <small style={{ color: colors.textLight, display: 'block', marginTop: '4px' }}>
                                            Used in ad copy generation. Editing here won&apos;t change the Style custom field in TFUN.
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Right Side - Campaign Settings Card and Preview Button */}
                        <div>
                            {/* Campaign Settings - Moved from left side */}
                            <div style={{
                                backgroundColor: colors.card,
                                borderRadius: '12px',
                                border: `1px solid ${colors.border}`,
                                padding: '24px',
                                marginBottom: '24px'
                            }}>
                                <h3 style={{
                                    color: colors.text,
                                    marginBottom: '16px',
                                    fontSize: '18px',
                                    fontWeight: '600'
                                }}>
                                    ⚙️ Campaign Settings
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '6px',
                                            fontWeight: '500',
                                            color: colors.text
                                        }}>
                                            Campaign Target *:
                                        </label>
                                        <select
                                            value={formData.campaign_target}
                                            onChange={(e) => handleInputChange('campaign_target', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontFamily: 'Poppins',
                                                backgroundColor: colors.card
                                            }}
                                        >
                                            <option value="">Select target audience...</option>
                                            <option value="Kids">Kids</option>
                                            <option value="Adults">Adults</option>
                                            <option value="Both">Both</option>
                                        </select>
                                        <small style={{ color: colors.textLight }}>
                                            Who is your primary target audience?
                                        </small>
                                    </div>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '6px',
                                            fontWeight: '500',
                                            color: colors.text
                                        }}>
                                            Business Type *:
                                        </label>
                                        <select
                                            value={formData.business_type}
                                            onChange={(e) => handleInputChange('business_type', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontFamily: 'Poppins',
                                                backgroundColor: colors.card
                                            }}
                                        >
                                            <option value="">Select business type...</option>
                                            <optgroup label="Martial Arts">
                                                <option value="Karate">Karate</option>
                                                <option value="Taekwondo">Taekwondo</option>
                                                <option value="Jiu-Jitsu/BJJ">Jiu-Jitsu/BJJ</option>
                                                <option value="MMA/Mixed Martial Arts">MMA/Mixed Martial Arts</option>
                                                <option value="Kickboxing (Martial Arts)">Kickboxing (Martial Arts)</option>
                                                <option value="Kung Fu">Kung Fu</option>
                                                <option value="Other Martial Arts">Other Martial Arts</option>
                                            </optgroup>
                                            <optgroup label="Gym/Fitness">
                                                <option value="CrossFit">CrossFit</option>
                                                <option value="Kickboxing (Fitness)">Kickboxing (Fitness)</option>
                                                <option value="General Fitness/Gym">General Fitness/Gym</option>
                                                <option value="Strength Training">Strength Training</option>
                                                <option value="Bootcamp/HIIT">Bootcamp/HIIT</option>
                                                <option value="Other Gym">Other Gym</option>
                                            </optgroup>
                                            <optgroup label="Dance Studio">
                                                <option value="Ballet">Ballet</option>
                                                <option value="Hip-Hop">Hip-Hop</option>
                                                <option value="Contemporary/Modern">Contemporary/Modern</option>
                                                <option value="Ballroom">Ballroom</option>
                                                <option value="Jazz">Jazz</option>
                                                <option value="Other Dance">Other Dance</option>
                                            </optgroup>
                                            <option value="Other">Other</option>
                                        </select>
                                        <small style={{ color: colors.textLight }}>
                                            What type of business is this?
                                        </small>
                                    </div>
                                    {formData.business_type === 'Other' && (
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: '6px',
                                                fontWeight: '500',
                                                color: colors.text
                                            }}>
                                                Specify Business Type *:
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.other_business_type}
                                                onChange={(e) => handleInputChange('other_business_type', e.target.value)}
                                                placeholder="e.g., Yoga Studio, Boxing Gym, etc."
                                                style={{
                                                    width: '100%',
                                                    padding: '12px',
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: '6px',
                                                    fontSize: '14px',
                                                    fontFamily: 'Poppins'
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '6px',
                                            fontWeight: '500',
                                            color: colors.text
                                        }}>
                                            Targeting Radius (miles):
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.targeting_radius}
                                            onChange={(e) => handleInputChange('targeting_radius', e.target.value)}
                                            min="1"
                                            max="50"
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontFamily: 'Poppins'
                                            }}
                                        />
                                        <small style={{ color: colors.textLight }}>
                                            Tip: Use what worked best on Facebook
                                        </small>
                                    </div>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '6px',
                                            fontWeight: '500',
                                            color: colors.text
                                        }}>
                                            Daily Budget ($):
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.budget}
                                            onChange={(e) => handleInputChange('budget', e.target.value)}
                                            min="10"
                                            max="1000"
                                            step="5"
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontFamily: 'Poppins'
                                            }}
                                        />
                                        <small style={{ color: colors.textLight }}>
                                            Recommended: $20-50 per day
                                        </small>
                                    </div>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: '6px',
                                            fontWeight: '500',
                                            color: colors.text
                                        }}>
                                            Landing Page URL:
                                        </label>
                                        <input
                                            type="url"
                                            value={formData.final_url}
                                            onChange={handleUrlChange}
                                            placeholder="https://www.yourstudio.com/"
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: `1px solid ${
                                                    urlValidation.valid === false ? colors.error : 
                                                    urlValidation.valid === true ? colors.success : 
                                                    colors.border
                                                }`,
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontFamily: 'Poppins'
                                            }}
                                        />
                                        <small style={{ 
                                            color: urlValidation.checking ? colors.warning :
                                                   urlValidation.valid === false ? colors.error :
                                                   urlValidation.valid === true ? colors.success :
                                                   colors.textLight
                                        }}>
                                            {urlValidation.checking ? '⏳ Checking URL...' :
                                             urlValidation.message || 'Where people will land when they click your ads'}
                                        </small>
                                    </div>
                                </div>
                            </div>

                            {/* Preview Button - Moved from left side */}
                            <button
                                onClick={() => {
                                    setSelectedCampaign(null); // Clear any selected campaign for new creation
                                    setModalOpen(true);
                                }}
                                disabled={!isFormValid()}
                                style={{
                                    width: '100%',
                                    backgroundColor: !isFormValid() ? '#cccccc' : colors.primary,
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    cursor: !isFormValid() ? 'not-allowed' : 'pointer',
                                    fontFamily: 'Poppins',
                                    fontWeight: '600',
                                    fontSize: '16px',
                                    transition: 'all 0.2s ease',
                                    opacity: !isFormValid() ? 0.7 : 1
                                }}
                            >
                                <span style={{ marginRight: '8px' }}>👀</span>
                                Preview Google Ads Campaign
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'previous' && (
                    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        <div style={{
                            backgroundColor: colors.card,
                            borderRadius: '12px',
                            border: `1px solid ${colors.border}`,
                            padding: '24px'
                        }}>
                            <h3 style={{
                                color: colors.text,
                                marginBottom: '20px',
                                fontSize: '20px',
                                fontWeight: '600'
                            }}>
                                📋 Previous Campaigns
                            </h3>
                            
                            {campaignsLoading ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <div style={{ fontSize: '32px', marginBottom: '16px' }}>⏳</div>
                                    <p style={{ color: colors.textLight }}>Loading previous campaigns...</p>
                                </div>
                            ) : previousCampaigns.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎯</div>
                                    <h4 style={{ color: colors.text, marginBottom: '8px' }}>No campaigns yet</h4>
                                    <p style={{ color: colors.textLight, marginBottom: '20px' }}>
                                        Create your first campaign to see it here
                                    </p>
                                    <button
                                        onClick={() => setActiveTab('create')}
                                        style={{
                                            backgroundColor: colors.primary,
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '12px 24px',
                                            cursor: 'pointer',
                                            fontFamily: 'Poppins',
                                            fontWeight: '600'
                                        }}
                                    >
                                        Create First Campaign
                                    </button>
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gap: '16px' }}>
                                    {previousCampaigns.map((campaign) => (
                                        <div
                                            key={campaign.id}
                                            style={{
                                                border: `1px solid ${colors.border}`,
                                                borderRadius: '8px',
                                                padding: '16px',
                                                backgroundColor: colors.secondary,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <div>
                                                <h4 style={{
                                                    color: colors.text,
                                                    margin: '0 0 4px 0',
                                                    fontSize: '16px',
                                                    fontWeight: '600'
                                                }}>
                                                    {campaign.campaign_name}
                                                </h4>
                                                <p style={{
                                                    color: colors.textLight,
                                                    margin: '0',
                                                    fontSize: '14px'
                                                }}>
                                                    Target: {campaign.campaign_target} • Created: {new Date(campaign.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={() => handleSelectCampaign(campaign.id)}
                                                    style={{
                                                        backgroundColor: colors.primary,
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '8px 16px',
                                                        cursor: 'pointer',
                                                        fontFamily: 'Poppins',
                                                        fontWeight: '500',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    👀 View
                                                </button>
                                                <button
                                                    onClick={() => handleSaveAsNew(campaign.id)}
                                                    style={{
                                                        backgroundColor: colors.success,
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '8px 16px',
                                                        cursor: 'pointer',
                                                        fontFamily: 'Poppins',
                                                        fontWeight: '500',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    📋 Save as New
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteCampaign(campaign.id, campaign.campaign_name)}
                                                    style={{
                                                        backgroundColor: colors.error,
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '8px 16px',
                                                        cursor: 'pointer',
                                                        fontFamily: 'Poppins',
                                                        fontWeight: '500',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    🗑️ Delete
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            
            {/* Google Ads Preview Modal */}
            <ErrorBoundary>
                <GoogleAdsPreviewModal
                    isOpen={modalOpen}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedCampaign(null);
                        // Reload previous campaigns if we were on that tab
                        if (activeTab === 'previous') {
                            loadPreviousCampaigns();
                        }
                    }}
                    locationId={locationId}
                    campaignParams={formData}
                    selectedCampaign={selectedCampaign} // Pass selected campaign data
                />
            </ErrorBoundary>
        </div>
    );
};

export default GoogleAdsGeneratorPage;